import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { formatPrice, statusColor, statusLabel } from '../../utils/helpers';
import toast from 'react-hot-toast';
import './AdminOrderDetail.css';

const AdminOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).finally(() => setLoading(false));
  }, [id]);

  const updateStatus = async (status) => {
    setUpdating(true);
    try {
      const { data } = await api.put(`/orders/${id}/status`, { status });
      setOrder(data);
      toast.success(`Order marked as ${status}`);
    } catch {
      toast.error('Update failed');
    } finally {
      setUpdating(false);
    }
  };

  const deleteOrder = async () => {
    if (!window.confirm('Delete this order permanently?')) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success('Order deleted');
      navigate('/admin/orders');
    } catch {
      toast.error('Delete failed');
    }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>;
  if (!order) return <div style={{ textAlign: 'center', padding: 80, color: 'var(--text3)' }}>Order not found</div>;

  return (
    <div className="admin-order-detail">
      <div className="aod-header">
        <div>
          <button className="back-btn" onClick={() => navigate('/admin/orders')}>← Back to Orders</button>
          <div className="admin-page-title">
            Order #{order._id.slice(-8).toUpperCase()}
          </div>
          <p className="admin-page-sub">{new Date(order.createdAt).toLocaleString()}</p>
        </div>
        <div className="aod-actions">
          <span className={`badge ${statusColor(order.orderStatus)}`} style={{ fontSize: '0.95rem', padding: '8px 20px' }}>
            {statusLabel(order.orderStatus)}
          </span>
        </div>
      </div>

      <div className="aod-grid">
        {/* Left - Order Details */}
        <div className="aod-main">
          {/* Items */}
          <div className="aod-card card">
            <h3>📦 Order Items</h3>
            {order.items?.map((item, i) => (
              <div key={i} className="aod-item">
                <img src={item.thumbnail || 'https://via.placeholder.com/70'} alt={item.name} />
                <div className="aod-item-info">
                  <span className="aod-item-name">{item.name}</span>
                  <span className="aod-item-meta">
                    Size: <strong>{item.size}</strong> &nbsp;×&nbsp; <strong>{item.quantity}</strong>
                  </span>
                  {item.isPreOrder && (
                    <span className="aod-preorder">
                      🔖 Pre-Order — {item.preOrderType === '50percent' ? '50% Advance' : '100% Advance'}
                    </span>
                  )}
                </div>
                <div className="aod-item-price">
                  <span>{formatPrice(item.price)}</span>
                  <small>each</small>
                  <strong>{formatPrice(item.price * item.quantity)}</strong>
                </div>
              </div>
            ))}
          </div>

          {/* Customer */}
          <div className="aod-card card">
            <h3>👤 Customer Details</h3>
            <div className="customer-detail-grid">
              <div className="cd-item">
                <span className="cd-label">Name</span>
                <span className="cd-val">{order.user?.name}</span>
              </div>
              <div className="cd-item">
                <span className="cd-label">Email</span>
                <span className="cd-val">{order.user?.email}</span>
              </div>
              <div className="cd-item">
                <span className="cd-label">Phone</span>
                <span className="cd-val">{order.user?.phone || order.shippingAddress?.phone}</span>
              </div>
              <div className="cd-item">
                <span className="cd-label">Address</span>
                <span className="cd-val">
                  {order.shippingAddress?.street}, {order.shippingAddress?.city}, {order.shippingAddress?.district}
                </span>
              </div>
              <div className="cd-item">
                <span className="cd-label">Location</span>
                <span className="cd-val">{order.shippingAddress?.insideDhaka ? '🏙️ Inside Dhaka' : '🌍 Outside Dhaka'}</span>
              </div>
            </div>
          </div>

          {order.note && (
            <div className="aod-card card">
              <h3>📝 Order Note</h3>
              <p style={{ color: 'var(--text3)', fontSize: '0.9rem' }}>{order.note}</p>
            </div>
          )}
        </div>

        {/* Right - Actions */}
        <div className="aod-sidebar">
          {/* Payment Summary */}
          <div className="aod-card card">
            <h3>💰 Payment Summary</h3>
            <div className="aod-totals">
              <div className="aod-row"><span>Subtotal</span><span>{formatPrice(order.subtotal)}</span></div>
              <div className="aod-row"><span>Shipping</span><span>{formatPrice(order.shippingCharge)}</span></div>
              {order.advancePaid > 0 && (
                <div className="aod-row"><span>Advance Paid</span><span className="advance-val">{formatPrice(order.advancePaid)}</span></div>
              )}
              <div className="aod-row total-row">
                <span>Total</span>
                <span className="aod-total">{formatPrice(order.totalPrice)}</span>
              </div>
            </div>
            <div className="aod-status-row">
              <span>Payment</span>
              <span className={`badge ${statusColor(order.paymentStatus)}`}>{statusLabel(order.paymentStatus)}</span>
            </div>
            {order.customerMarkedPaid && !order.adminVerifiedPaid && (
              <div className="verify-alert">
                ⚠️ Customer has marked this order as paid. Please verify and click "Paid" below.
              </div>
            )}
            {order.shippingReceived && (
              <div className="shipping-received-badge">
                ✅ Shipping charge verified & received
              </div>
            )}
            {order.paymentSenderSubmitted && order.senderNumber && (
              <div className="sender-info-box">
                <span className="sib-label">💳 Customer sent from</span>
                <span className="sib-number">{order.senderNumber}</span>
              </div>
            )}
          </div>

          {/* Admin Actions */}
          <div className="aod-card card">
            <h3>⚡ Admin Actions</h3>
            <p className="action-note">Update order status to notify the customer.</p>

            <div className="admin-order-btns">
              <button
                className="btn aob-shipping"
                disabled={updating || order.shippingReceived}
                onClick={() => updateStatus('shipping_received')}
                title="Mark delivery fee as received"
              >
                💰 Shipping Charge Received
              </button>
              <button
                className="btn aob-sent"
                disabled={updating || order.orderStatus === 'sent'}
                onClick={() => updateStatus('sent')}
              >
                🚚 Mark as Sent
              </button>
              <button
                className="btn aob-paid"
                disabled={updating || order.orderStatus === 'paid'}
                onClick={() => updateStatus('paid')}
              >
                ✅ Mark as Paid
              </button>
              <button
                className="btn aob-cancel"
                disabled={updating || order.orderStatus === 'cancelled'}
                onClick={() => updateStatus('cancelled')}
              >
                ❌ Cancel Order
              </button>
            </div>

            <div className="aod-status-timeline">
              {['processing', 'sent', 'paid'].map((s, i) => (
                <div key={s} className={`timeline-step ${order.orderStatus === s ? 'current' : ['processing','sent','paid','delivered'].indexOf(order.orderStatus) > i ? 'done' : ''}`}>
                  <div className="ts-dot" />
                  <span>{statusLabel(s)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Danger */}
          <div className="aod-card card danger-card">
            <h3>⚠️ Danger Zone</h3>
            <button className="btn btn-danger" style={{ width: '100%', justifyContent: 'center' }} onClick={deleteOrder}>
              🗑️ Delete Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetail;
